<template>
  <div class="pt-4">
    <div class="inner py-0">
      <h1>Line Statuses</h1>
    </div>

    <div class="inner flex items-center justify-end gap-4 py-2">
      <label class="search-bar input input-sm flex min-w-96 items-center gap-2">
        <input
          ref="searchInputRef"
          type="text"
          placeholder="Search"
          class="grow"
          v-model="filterText"
          autocomplete="off"
          @keyup="handleSearchKeyUp" />
        <XIcon
          v-if="filterText && filterText.length > 0"
          :size="20"
          class="cursor-pointer"
          @click="filterText = ''" />
        <SearchIcon :size="20" />
      </label>
    </div>

    <div class="inner pt-0">
      <Alert
        v-if="!loadingData && error"
        type="error">
        {{ error }}
      </Alert>

      <Skeleton
        v-if="loadingData && !error"
        shape="rectangle"
        width="100%"
        height="300px"
        class="rounded-md" />

      <table
        v-else-if="!error"
        v-tablesort
        id="line-status-list"
        class="apptable dense mt-2">
        <thead>
          <tr>
            <td
              data-test="source-status-key"
              class="text-left">
              Source Status Key
            </td>
            <td
              data-test="name"
              class="text-left sorted ascending">
              Name
            </td>
            <td
              data-test="non-recurring"
              class="text-center">
              Non-Recurring
            </td>
            <td
              data-test="snapshot-date"
              class="date">
              Snapshot Date
            </td>
            <td
              data-test="actions"
              class="nosort actions" />
          </tr>
        </thead>
        <tbody>
          <tr
            v-if="filteredLineStatuses.length === 0"
            data-test="empty-row">
            <td
              colspan="5"
              class="nosort text-center italic">
              {{ lineStatuses.length === 0 ? 'No line statuses found.' : 'No line statuses match your search.' }}
            </td>
          </tr>
          <tr
            v-for="lineStatus in filteredLineStatuses"
            :key="lineStatus.ID"
            data-test="row"
            class="group">
            <td :data-test="`${lineStatus.ID}-source-status-key`">
              {{ lineStatus.SourceStatusKey }}
            </td>
            <td :data-test="`${lineStatus.ID}-name`">
              {{ lineStatus.Code }}
            </td>
            <td
              :data-test="`${lineStatus.ID}-non-recurring`"
              class="text-center">
              <input
                type="checkbox"
                class="checkbox checkbox-sm checkbox-primary align-middle"
                :checked="lineStatus.NonRecurring"
                disabled />
            </td>
            <td
              :data-test="`${lineStatus.ID}-snapshot-date`"
              class="date">
              {{ formatDate(lineStatus.SnapshotDate) }}
            </td>
            <td
              :data-test="`${lineStatus.ID}-actions`"
              class="actions">
              <button
                type="button"
                class="inline-flex cursor-pointer p-1 opacity-30 transition-opacity group-hover:opacity-100"
                :data-test="`edit-line-status-${lineStatus.ID}`"
                title="Edit line status"
                @click="editingLineStatus = lineStatus">
                <Edit2 :size="16" />
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <LineStatusEditModal
      v-if="editingLineStatus"
      :line-status="editingLineStatus"
      :show="true"
      :on-save="submitLineStatusChange"
      :on-delete="submitLineStatusDelete"
      @closed="editingLineStatus = null" />
  </div>
</template>

<script lang="ts">
import { lineStatusService } from '@/services/api';
import LineStatusEditModal from '@/components/LineStatusEditModal.vue';
import { LineStatus } from '@/types/LineStatus';
import { useToast } from '@/composables/useToast';
import Alert from '@/components/v2/Alert.vue';
import Skeleton from '@/components/v2/Skeleton.vue';
import { Edit2, SearchIcon, XIcon } from '@lucide/vue';
import { DateTime } from 'luxon';

export default {
  name: 'LineStatus',
  components: {
    LineStatusEditModal,
    Alert,
    Skeleton,
    SearchIcon,
    XIcon,
    Edit2,
  },
  setup() {
    return { toast: useToast() };
  },
  data: () => ({
    loadingData: true,
    error: undefined as string | undefined,
    filterText: '',
    lineStatuses: [] as LineStatus[],
    editingLineStatus: null as LineStatus | null,
  }),
  computed: {
    filteredLineStatuses(): LineStatus[] {
      if (!this.filterText) {
        return this.lineStatuses;
      }

      const searchText = this.filterText.toLowerCase();
      return this.lineStatuses.filter(
        (item) => item.SourceStatusKey.toLowerCase().includes(searchText) || item.Code?.toLowerCase().includes(searchText),
      );
    },
  },
  mounted() {
    if (this.$refs.searchInputRef) {
      (this.$refs.searchInputRef as HTMLInputElement).focus();
    }
  },
  methods: {
    handleSearchKeyUp(e: KeyboardEvent) {
      if (e.key && e.key === 'Escape') {
        this.filterText = '';
        e.stopPropagation();
      }
    },
    formatDate(date: string | null): string {
      if (!date) {
        return '--';
      }
      return DateTime.fromISO(date).toFormat('M/d/yyyy');
    },
    async refreshData() {
      try {
        const lineStatuses = await lineStatusService.list();
        this.lineStatuses = (lineStatuses || []).sort((a, b) => (a.Code ?? '').localeCompare(b.Code ?? ''));
        this.error = undefined;
      } catch {
        this.error = 'Failed to load line statuses.';
      } finally {
        this.loadingData = false;
      }
    },
    async submitLineStatusChange(id: number, code: string, nonRecurring: boolean) {
      try {
        await lineStatusService.update(id, { Code: code.trim(), NonRecurring: nonRecurring });
        this.toast.success('Line status updated.');
        await this.refreshData();
      } catch {
        this.toast.error('Failed to update line status.');
      }
    },
    async submitLineStatusDelete(id: number) {
      try {
        await lineStatusService.remove(id);
        this.toast.success('Line status deleted.');
        await this.refreshData();
      } catch {
        this.toast.error('Failed to delete line status.');
      }
    },
  },
  created() {
    this.refreshData();
  },
};
</script>
